// ─── Sous-module « inv-referentiel » — référentiel produits ───────
// Fiche produit réutilisable, identifiée par son CODE-BARRES. Deux sources :
//   • Open Food Facts (base publique, lecture seule) — nom, marque, photo, nutrition
//   • saisie de l'utilisateur — ce qu'il corrige/complète prime sur OFF
//
// Le référentiel est rattaché à l'ESPACE : chaque foyer se constitue sa base au
// fil des scans, sans dépendre du réseau au deuxième passage.
//   produits/{id} = { espaceId, code, nom, marque, photoUrl, nutrition, source,
//                     majLe, majPar }
//
// Prochaine étape (roadmap « référentiel partagé ») : promouvoir les fiches
// perso vers une base commune à toutes les apps de la suite. Le modèle est déjà
// au format OFF pour que l'export/import reste direct.
import { db } from '@mop/core';
import {
  collection, doc, addDoc, updateDoc, onSnapshot, query, where, getDocs, limit,
} from 'firebase/firestore';
import { chercherProduit, codeBarresValide } from './openFoodFacts';
import { chercherParCodeBarres, estISBN } from './produitsPublics';

export { chercherProduit, codeBarresValide, chercherParCodeBarres, estISBN };

// Toutes les fiches de l'espace (temps réel).
export function ecouterProduits(tenantId, callback, onErreur) {
  if (!tenantId) { callback([]); return () => {}; }
  const q = query(collection(db, 'produits'), where('espaceId', '==', tenantId));
  return onSnapshot(
    q,
    (snap) => {
      const l = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      l.sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
      callback(l);
    },
    (e) => { callback([]); onErreur?.(e); },
  );
}

// Fiche locale par code-barres (lecture ponctuelle).
export async function produitParCode(tenantId, code) {
  if (!tenantId || !code) return null;
  const q = query(
    collection(db, 'produits'),
    where('espaceId', '==', tenantId), where('code', '==', String(code)), limit(1),
  );
  const snap = await getDocs(q);
  const d = snap.docs[0];
  return d ? { id: d.id, ...d.data() } : null;
}

export async function enregistrerProduit(tenantId, fiche, uid) {
  if (!tenantId || !fiche?.code) return null;
  const existante = await produitParCode(tenantId, fiche.code);
  const donnees = {
    espaceId:  tenantId,
    code:      String(fiche.code),
    nom:       (fiche.nom || '').trim(),
    marque:    (fiche.marque || '').trim(),
    auteur:    (fiche.auteur || '').trim(),          // livres : auteur
    editeur:   (fiche.editeur || '').trim(),         // livres : éditeur
    annee:     fiche.annee || null,                  // livres : année de parution
    pages:     fiche.pages || null,                  // livres : nombre de pages
    photoUrl:  fiche.photoUrl || null,
    nutrition: fiche.nutrition || null,
    contenance:      fiche.contenance ?? null,
    contenanceUnite: fiche.contenanceUnite || '',
    source:    fiche.source || 'off',                // off | google-books | open-library | local
    majLe:     new Date().toISOString(),
    majPar:    uid || null,
  };
  if (existante) { await updateDoc(doc(db, 'produits', existante.id), donnees); return existante.id; }
  const ref = await addDoc(collection(db, 'produits'), { ...donnees, creeLe: donnees.majLe });
  return ref.id;
}

// Résolution d'un code scanné : le référentiel de l'espace D'ABORD (instantané,
// et il porte les corrections de l'utilisateur), puis les bases PUBLIQUES avec
// routage automatique — ISBN (978/979) → Google Books puis Open Library, sinon
// Open Food Facts. `{ fiche, origine: 'local'|'off'|'google-books'|'open-library'|null }`
export async function resoudreCode(tenantId, code, { uid } = {}) {
  const c = String(code || '').trim();
  if (!codeBarresValide(c)) return { fiche: null, origine: null, raison: 'code' };

  const locale = await produitParCode(tenantId, c).catch(() => null);
  if (locale) return { fiche: locale, origine: 'local' };

  const r = await chercherParCodeBarres(c);       // multi-base (peut lever : réseau)
  if (!r.trouve) return { fiche: null, origine: null, raison: r.raison || 'inconnu' };

  const fiche = {
    code: c, nom: r.nom || '', marque: r.marque || '', auteur: r.auteur || '',
    editeur: r.editeur || '', annee: r.annee || null, pages: r.pages || null,
    photoUrl: r.photoUrl || null, nutrition: r.nutrition || null, source: r.source || 'off',
  };
  // On mémorise pour le prochain scan (échec silencieux : le scan reste utile).
  try { await enregistrerProduit(tenantId, fiche, uid); } catch (_) {}
  return { fiche, origine: r.source || 'off' };
}
